import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  ChevronLeft,
  Clock,
  ExternalLink,
  Mail,
  MailX,
  RefreshCw,
  User as UserIcon,
} from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { LoadingState } from '@/components/app/LoadingState';
import { ErrorState } from '@/components/app/ErrorState';
import { CodeBlock } from '@/components/app/CodeBlock';
import { CopyableId } from '@/components/app/CopyableId';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { humanizeError } from '@/lib/apiClient';
import { shortId } from '@/lib/utils';
import { useDecisionDetail } from '../hooks';
import { outcomeBadgeVariant } from '../decisionOutcome';
import type { DecisionDetail, DecisionDetailUser } from '../api/notificationsApi';

function deliveryVariant(s?: string): 'success' | 'warning' | 'outline' | 'destructive' | 'default' {
  if (s === 'sent') return 'success';
  if (s === 'partial') return 'warning';
  if (s === 'failed') return 'destructive';
  if (s === 'pending') return 'outline';
  return 'default';
}

function formatRelative(iso?: string): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
}

export function DecisionDetailPage() {
  const { decisionId = '' } = useParams<{ decisionId: string }>();
  const { data, isLoading, isError, error, refetch } = useDecisionDetail(decisionId);

  return (
    <div className="space-y-6">
      <Link
        to="/notifications/health"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Back to health
      </Link>

      {isLoading && <LoadingState label="Loading decision" />}
      {isError && (
        <ErrorState
          title="Decision unavailable"
          description={humanizeError(error)}
          actionLabel="Retry"
          onAction={() => refetch()}
        />
      )}
      {!isLoading && !isError && data && (
        <DecisionBody decision={data.decision} user={data.user} />
      )}
    </div>
  );
}

function DecisionBody({
  decision,
  user,
}: {
  decision: DecisionDetail;
  user: DecisionDetailUser | null;
}) {
  const titleText = decision.title || decision.notificationType;
  const sentLabel = decision.outcome === 'send' ? 'Sent' : decision.outcome === 'skip' ? 'Skipped' : 'Deferred';

  return (
    <>
      <PageHeader
        title={titleText}
        description={
          <span className="inline-flex flex-wrap items-center gap-2">
            <Badge variant={outcomeBadgeVariant(decision.outcome)}>{sentLabel}</Badge>
            <span>{decision.notificationType}</span>
            {decision.category && <span>· {decision.category}</span>}
            <span aria-hidden>·</span>
            <CopyableId value={decision.decisionId} display={shortId(decision.decisionId, 8, 6)} label="Decision ID" />
          </span>
        }
      />

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <UserCard user={user} fallbackUserId={decision.userId} />
        <ContentCard decision={decision} />
        <DecisionCard decision={decision} />
      </section>

      <EngagementCard decision={decision} />
      <TimelineCard decision={decision} />

      {(decision.outcome === 'defer' || decision.retryState || decision.nextAttemptAt) && (
        <DeferCard decision={decision} />
      )}

      {decision.hasUnresolvedPlaceholders && (
        <PlaceholderWarningCard decision={decision} />
      )}

      {decision.data && Object.keys(decision.data).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Request data payload</CardTitle>
            <CardDescription>
              The <code>data</code> object the requester passed in. Used for variable interpolation and the
              client-side route on tap.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CodeBlock value={decision.data} />
          </CardContent>
        </Card>
      )}
    </>
  );
}

function UserCard({ user, fallbackUserId }: { user: DecisionDetailUser | null; fallbackUserId: string }) {
  const userId = user?.userId ?? fallbackUserId;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <UserIcon className="h-4 w-4" /> User
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <Row label="Email" value={user?.email ?? '—'} />
        <Row label="Display name" value={user?.displayName ?? '—'} />
        <Row label="Timezone" value={user?.timeZone ?? '—'} />
        <Separator className="my-2" />
        <div className="flex flex-wrap items-center gap-2">
          <CopyableId value={userId} display={shortId(userId, 8, 6)} label="User ID" />
          <Link
            to={`/users/${encodeURIComponent(userId)}`}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            User profile <ExternalLink className="h-3 w-3" />
          </Link>
          <Link
            to={`/notifications/users/${encodeURIComponent(userId)}`}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            Notification inspector <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

function ContentCard({ decision }: { decision: DecisionDetail }) {
  const hasContent = !!(decision.title || decision.body);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Bell className="h-4 w-4" /> Content
        </CardTitle>
        <CardDescription>
          Rendered text the user would have received. Empty for skipped sends with no template.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {hasContent ? (
          <>
            {decision.title && (
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Title</div>
                <div className="mt-0.5 whitespace-pre-wrap font-medium">{decision.title}</div>
              </div>
            )}
            {decision.body && (
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Body</div>
                <div className="mt-0.5 whitespace-pre-wrap">{decision.body}</div>
              </div>
            )}
          </>
        ) : (
          <p className="text-muted-foreground">No rendered title/body recorded for this decision.</p>
        )}
        {decision.variantId && <Row label="Variant" value={<code className="font-mono text-xs">{decision.variantId}</code>} />}
      </CardContent>
    </Card>
  );
}

function DecisionCard({ decision }: { decision: DecisionDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Decision</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <Row label="Outcome" value={<Badge variant={outcomeBadgeVariant(decision.outcome)}>{decision.outcome}</Badge>} />
        <Row label="Channel" value={decision.channel ?? '—'} />
        <Row label="Priority" value={decision.priority ?? '—'} />
        <Row label="Triggered by" value={decision.triggeredBy ? <code className="font-mono text-xs">{decision.triggeredBy}</code> : '—'} />
        {(decision.reasonCodes?.length ?? 0) > 0 && (
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Reason codes</div>
            <div className="mt-1 flex flex-wrap gap-1">
              {decision.reasonCodes!.map((rc) => (
                <Badge key={rc} variant="outline" className="font-mono text-[10px]">
                  {rc}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {(decision.journeyId || decision.experimentId) && <Separator className="my-2" />}
        {decision.journeyId && (
          <Row
            label="Journey"
            value={
              <Link
                to={`/notifications/journeys/${encodeURIComponent(decision.journeyId)}`}
                className="text-primary hover:underline"
              >
                {decision.journeyId}
                {typeof decision.journeyStep === 'number' ? ` · step ${decision.journeyStep}` : ''}
              </Link>
            }
          />
        )}
        {decision.experimentId && (
          <Row
            label="Experiment"
            value={
              <span className="font-mono text-xs">
                {decision.experimentId}
                {decision.experimentVariant ? ` · ${decision.experimentVariant}` : ''}
              </span>
            }
          />
        )}
      </CardContent>
    </Card>
  );
}

function EngagementCard({ decision }: { decision: DecisionDetail }) {
  const isSend = decision.outcome === 'send';
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          {decision.opened ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <MailX className="h-4 w-4 text-muted-foreground" />}
          Engagement
        </CardTitle>
        <CardDescription>
          {isSend
            ? 'Open & action signals come from the client when the user taps the notification.'
            : 'Not delivered — engagement signals do not apply.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
        <Row
          label="Opened"
          value={
            decision.opened ? (
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" /> Yes
              </span>
            ) : isSend ? (
              <span className="text-muted-foreground">No signal yet</span>
            ) : (
              '—'
            )
          }
        />
        <Row
          label="Opened at"
          value={
            decision.openedAt ? (
              <span title={decision.openedAt}>{formatRelative(decision.openedAt)}</span>
            ) : (
              '—'
            )
          }
        />
        <Row
          label="Action taken"
          value={
            decision.actionTaken ? (
              <span className="inline-flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Yes
                {decision.actionType ? <code className="ml-1 font-mono text-xs">{decision.actionType}</code> : null}
              </span>
            ) : (
              '—'
            )
          }
        />
        <Row
          label="Action at"
          value={
            decision.actionTakenAt ? (
              <span title={decision.actionTakenAt}>{formatRelative(decision.actionTakenAt)}</span>
            ) : (
              '—'
            )
          }
        />
        <Separator className="my-1 sm:col-span-2" />
        <Row
          label="Delivery status"
          value={
            decision.deliveryStatus ? (
              <Badge variant={deliveryVariant(decision.deliveryStatus)}>{decision.deliveryStatus}</Badge>
            ) : (
              '—'
            )
          }
        />
        <Row
          label="Push success / failure"
          value={
            decision.pushSuccessCount != null || decision.pushFailureCount != null
              ? `${decision.pushSuccessCount ?? 0} / ${decision.pushFailureCount ?? 0}`
              : '—'
          }
        />
        <Row
          label="Inbox written"
          value={
            decision.inboxWritten === true ? (
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <Mail className="h-3.5 w-3.5" /> Yes
              </span>
            ) : decision.inboxWritten === false ? (
              <span className="text-muted-foreground">No</span>
            ) : (
              '—'
            )
          }
        />
      </CardContent>
    </Card>
  );
}

function TimelineCard({ decision }: { decision: DecisionDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="h-4 w-4" /> Timeline
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <Row label="Requested" value={absRel(decision.requestedAt)} />
        <Row label="Decided" value={absRel(decision.decidedAt)} />
        <Row label="Sent" value={absRel(decision.sentAt)} />
        {decision.openedAt && <Row label="Opened" value={absRel(decision.openedAt)} />}
        {decision.actionTakenAt && <Row label="Action taken" value={absRel(decision.actionTakenAt)} />}
      </CardContent>
    </Card>
  );
}

function DeferCard({ decision }: { decision: DecisionDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <RefreshCw className="h-4 w-4" /> Defer / retry
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <Row label="Next attempt" value={absRel(decision.nextAttemptAt)} />
        <Row label="Retry count" value={decision.retryCount ?? '—'} />
        {decision.originalRequestId && (
          <Row
            label="Original request"
            value={<code className="font-mono text-xs">{decision.originalRequestId}</code>}
          />
        )}
        {decision.retryState && (
          <>
            <Separator className="my-2" />
            <Row label="Retry state" value={<Badge variant="outline">{decision.retryState.status}</Badge>} />
            <Row label="Attempts" value={decision.retryState.attemptCount} />
            {decision.retryState.lastAttemptAt && (
              <Row label="Last attempt" value={absRel(decision.retryState.lastAttemptAt)} />
            )}
            {decision.retryState.lastError && (
              <Row label="Last error" value={<code className="font-mono text-xs">{decision.retryState.lastError}</code>} />
            )}
            {decision.retryState.cancelReason && (
              <Row label="Cancel reason" value={decision.retryState.cancelReason} />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PlaceholderWarningCard({ decision }: { decision: DecisionDetail }) {
  return (
    <Card className="border-amber-300/70">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-amber-700">
          <AlertTriangle className="h-4 w-4" /> Unresolved placeholders
        </CardTitle>
        <CardDescription>
          The renderer detected variables that weren&apos;t supplied by the request. Users may have seen
          raw <code>{'{{name}}'}</code>-style tokens.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-1">
          {(decision.missingPlaceholderKeys ?? []).map((k) => (
            <Badge key={k} variant="warning" className="font-mono text-[10px]">
              {k}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right text-sm">{value}</span>
    </div>
  );
}

function absRel(iso?: string): React.ReactNode {
  if (!iso) return '—';
  return (
    <span title={iso}>
      {formatRelative(iso)} <span className="text-muted-foreground">· {iso}</span>
    </span>
  );
}
